import { useEffect, useRef } from 'react';

export function useMetronome(bpm: number, enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const nextTickRef = useRef<number>(0);

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!enabled) return;

    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext();
    }
    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const interval = 60 / bpm;
    nextTickRef.current = ctx.currentTime + 0.05;

    const tick = (time: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      gain.gain.setValueAtTime(0.35, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.035);
      osc.start(time);
      osc.stop(time + 0.035);
    };

    const schedule = () => {
      const c = ctxRef.current;
      if (!c) return;
      // schedule up to 100ms ahead to compensate for setTimeout jitter
      while (nextTickRef.current < c.currentTime + 0.1) {
        tick(nextTickRef.current);
        nextTickRef.current += interval;
      }
      timerRef.current = window.setTimeout(schedule, 25);
    };

    schedule();

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [bpm, enabled]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      ctxRef.current?.close();
    };
  }, []);
}
